// frontend/src/components/profile/MyStoreInventory.js
import React, { useState, useEffect, useCallback } from 'react';
import { partsApi } from '../../api/parts';
import { storesApi } from '../../api/stores';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import helpers from '../../utils/helpers';
import CameraModal from '../common/CameraModal';
import BoostModal from '../common/BoostModal'; 
import { useAuth } from '../../context/AuthContext'; 
import { settingsApi } from '../../api/settings'; 
import { 
    FaPlus, FaEdit, FaTrash, FaBoxOpen, FaSpinner, FaTimes, FaSave, 
    FaWeightHanging, FaCamera, FaFolderOpen, FaStore, FaRocket, FaTags, FaCalculator 
} from 'react-icons/fa';

const CATEGORY_GROUPS = {
    "Engine Components": [
        { id: 'engine_components', label: 'General Engine Parts' },
        { id: 'air_filters', label: 'Air Filters' },
        { id: 'oil_filters', label: 'Oil Filters' },
        { id: 'spark_plugs', label: 'Spark Plugs' },
        { id: 'ignition_coils', label: 'Ignition Coils' },
        { id: 'belts_hoses', label: 'Belts & Hoses' },
        { id: 'gaskets_seals', label: 'Gaskets & Seals' },
        { id: 'pistons_rings', label: 'Pistons & Rings' }
    ],
    "Brake System": [
        { id: 'brake_system', label: 'General Brake Parts' },
        { id: 'brake_pads', label: 'Brake Pads' },
        { id: 'brake_rotors', label: 'Brake Rotors/Discs' },
        { id: 'brake_calipers', label: 'Brake Calipers' },
        { id: 'brake_fluid', label: 'Brake Fluid' },
        { id: 'brake_shoes', label: 'Brake Shoes' }
    ],
    "Suspension & Steering": [
        { id: 'suspension_steering', label: 'General Suspension' },
        { id: 'shock_absorbers', label: 'Shock Absorbers & Struts' },
        { id: 'control_arms', label: 'Control Arms' },
        { id: 'ball_joints', label: 'Ball Joints' },
        { id: 'tie_rod_ends', label: 'Tie Rod Ends' },
        { id: 'steering_racks', label: 'Steering Racks' }
    ],
    "Transmission & Drivetrain": [
        { id: 'transmission_drivetrain', label: 'General Transmission' },
        { id: 'clutch_kits', label: 'Clutch Kits' },
        { id: 'cv_joints_axles', label: 'CV Joints & Axles' },
        { id: 'transmission_fluid', label: 'Transmission Fluid' }
    ],
    "Electrical System": [
        { id: 'electrical_system', label: 'General Electrical' },
        { id: 'batteries', label: 'Batteries' },
        { id: 'alternators', label: 'Alternators' },
        { id: 'starters', label: 'Starters' },
        { id: 'sensors', label: 'Sensors (O2, MAF, etc.)' },
        { id: 'fuses_relays', label: 'Fuses & Relays' }
    ],
    "Body & Exterior": [
        { id: 'body_exterior', label: 'General Body Parts' },
        { id: 'headlights', label: 'Headlights' },
        { id: 'tail_lights', label: 'Tail Lights' },
        { id: 'bumpers', label: 'Bumpers' },
        { id: 'mirrors', label: 'Mirrors' },
        { id: 'wipers', label: 'Wiper Blades' }
    ],
    "Tires & Wheels": [
        { id: 'tires_wheels', label: 'General Tires/Wheels' },
        { id: 'tires', label: 'Tires' },
        { id: 'wheels_rims', label: 'Wheels & Rims' },
        { id: 'wheel_bearings', label: 'Wheel Bearings & Hubs' }
    ],
    "Fluids & Chemicals": [
        { id: 'fluids_chemicals', label: 'General Fluids' },
        { id: 'engine_oil', label: 'Engine Oil' },
        { id: 'coolant', label: 'Coolant / Antifreeze' }
    ],
    "Other Systems": [
        { id: 'cooling_heating', label: 'Cooling & Heating (HVAC)' },
        { id: 'exhaust_system', label: 'Exhaust System' },
        { id: 'fuel_system', label: 'Fuel System' },
        { id: 'interior_components', label: 'Interior Components' },
        { id: 'tools_equipment', label: 'Tools & Equipment' }
    ]
};

const PART_BRANDS = [
    { id: 'toyota_genuine', label: 'Toyota Genuine Parts' },
    { id: 'honda_oem', label: 'Honda OEM' },
    { id: 'ford_motorcraft', label: 'Ford Motorcraft' },
    { id: 'mercedes_benz_genuine', label: 'Mercedes-Benz Genuine' },
    { id: 'bmw_oem', label: 'BMW OEM' },
    { id: 'denso', label: 'Denso' },
    { id: 'bosch', label: 'Bosch' },
    { id: 'ngk', label: 'NGK' },
    { id: 'valeo', label: 'Valeo' },
    { id: 'kyb', label: 'KYB' },
    { id: 'mobil_1', label: 'Mobil 1' },
    { id: 'total', label: 'Total' },
    { id: 'michelin', label: 'Michelin' },
    { id: 'rana_motors', label: 'Rana Motors' },
    { id: 'union_autoparts', label: 'Union Autoparts' },
    { id: 'kaydee', label: 'Kaydee' },
    { id: 'lucas_tvs', label: 'Lucas TVS' },
    { id: 'proforce', label: 'Proforce' },
    { id: 'bat', label: 'BAT' },
    { id: 'zikoko', label: 'Zikoko' },
];

const IMAGE_SLOTS = [
    { key: 'front_jpg', label: 'Primary Product View *' },
    { key: 'label_jpg', label: 'Box / Label View (Part #)' },
    { key: 'connector_jpg', label: 'Connector / Mounting View' },
    { key: 'stamp_jpg', label: 'Part Number Stamp (Engraving)' }
];

const initialFormState = {
    itemname: '',
    part_number: '',
    price: '',
    discount_percentage: 0, // <--- NEW FIELD
    make: '',
    model: '',
    year_range: '',
    stock_quantity: 1,
    description: '',
    category_id: 'engine_components',
    brand_id: PART_BRANDS[0].id, 
    custom_category: '', 
    custom_brand: '',    
    images: {}, 
    store_id: '', 
    pay_on_delivery_status: 'Available', 
    refundable_status: 'Refundable',     
    weight_kg: '',
    is_oem: false,
    condition: 'New (In Box)'
};

function MyStoreInventory() {
  const { user } = useAuth(); 
  const [parts, setParts] = useState([]);
  const [myStores, setMyStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [premiumConfig, setPremiumConfig] = useState({}); 
  const [mediaConfig, setMediaConfig] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [isCustomBrand, setIsCustomBrand] = useState(false);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Camera & Drafts State
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [activeViewType, setActiveViewType] = useState(null);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  // Boost Modal State
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [boostTarget, setBoostTarget] = useState(null);

  const fetchInventory = useCallback(async () => {
    try {
      const [partsData, storesData, settingsData] = await Promise.all([
          partsApi.getMyParts(),
          storesApi.getMyStores(),
          settingsApi.getAllSettings() 
      ]);
      
      if (Array.isArray(partsData)) {
          const validParts = partsData.filter(item => item && typeof item === 'object');
          setParts(validParts);
      } else {
          setParts([]);
      }
      setMyStores(storesData || []);
      setPremiumConfig(settingsData.premium_config || {}); 
      setMediaConfig(settingsData.media_config || {});
      
    } catch (err) {
      console.error("Error fetching inventory:", err.message);
      setError("Failed to load your inventory.");
      setParts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleOpenModal = (part = null) => {
    if (part) {
        setEditingPart(part);
        const isCatCustom = part.custom_category ? true : false;
        const isBrandCustom = part.custom_brand ? true : false;
        setIsCustomCategory(isCatCustom);
        setIsCustomBrand(isBrandCustom);
        setFormData({
            ...initialFormState,
            ...part,
            price: part.price || '',
            discount_percentage: part.discount_percentage || 0, // <--- LOAD DISCOUNT
            stock_quantity: part.stock_quantity || 0,
            weight_kg: part.weight_kg || '',
            is_oem: part.is_oem || false,
            condition: part.condition || 'New (In Box)',
            description: part.description || '',
            part_number: part.part_number || '',
            brand_id: isBrandCustom ? '' : (part.brand_id || PART_BRANDS[0].id),
            category_id: isCatCustom ? '' : (part.category_id || 'engine_components'),
            custom_category: part.custom_category || '',
            custom_brand: part.custom_brand || '',
            images: part.images || {},
            store_id: part.store_id || (myStores.length > 0 ? myStores[0].id : ''),
            pay_on_delivery_status: part.pay_on_delivery_status || 'Available',
            refundable_status: part.refundable_status || 'Refundable'
        });
    } else {
        setEditingPart(null);
        setFormData({
            ...initialFormState,
            store_id: myStores.length > 0 ? myStores[0].id : ''
        });
        setIsCustomCategory(false);
        setIsCustomBrand(false);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPart(null);
    setFormData(initialFormState);
    setIsCustomCategory(false);
    setIsCustomBrand(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'category_id') {
        if (value === 'other') {
            setIsCustomCategory(true);
            setFormData(prev => ({ ...prev, category_id: '', custom_category: '' }));
        } else {
            setIsCustomCategory(false);
            setFormData(prev => ({ ...prev, category_id: value, custom_category: '' }));
        }
    } else if (name === 'brand_id') {
        if (value === 'other') {
            setIsCustomBrand(true);
            setFormData(prev => ({ ...prev, brand_id: '', custom_brand: '' }));
        } else {
            setIsCustomBrand(false);
            setFormData(prev => ({ ...prev, brand_id: value, custom_brand: '' }));
        }
    } else {
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  // --- CAMERA & UPLOAD LOGIC ---
  const handleCameraClick = (viewType) => {
      const currentImageCount = Object.values(formData.images).filter(v => v).length;
      const freeLimit = premiumConfig?.free_image_limit || 4;
      const isPremium = user?.roles?.includes('admin') || user?.roles?.includes('super_admin');
      
      if (!isPremium && currentImageCount >= freeLimit) {
          const price = premiumConfig?.snap_and_save_unlock_price || 5000;
          alert(`Limit Reached! You can only upload ${freeLimit} images on the free plan. Please purchase Listing Credits to add more. Price: NGN ${price}`);
          return;
      }

      setActiveViewType(viewType);
      setIsCameraOpen(true);
  };

  const handleCameraCapture = async (file) => {
      await processFile(file, activeViewType);
      setIsCameraOpen(false);
  };

  const processFile = async (file, viewType) => {
      if (!file) return;
      setUploadingSlot(viewType);
      setUploading(true);
      try {
          const compressionQuality = mediaConfig?.parts_compression || 0.6;
          const compressed = await compressImage(file, compressionQuality);
          const response = await uploadService.uploadImage(compressed, 'part-images');
          setFormData(prev => ({
              ...prev,
              images: { ...prev.images, [viewType]: response.url }
          }));
      } catch (err) {
          alert("Image upload failed.");
      } finally {
          setUploading(false);
          setUploadingSlot(null);
      }
  };

  const handleFileInput = (e, viewType) => {
      const currentImageCount = Object.values(formData.images).filter(v => v).length;
      const freeLimit = premiumConfig?.free_image_limit || 4;
      const isPremium = user?.roles?.includes('admin') || user?.roles?.includes('super_admin');
      
      if (!isPremium && currentImageCount >= freeLimit) {
          const price = premiumConfig?.snap_and_save_unlock_price || 5000;
          alert(`Limit Reached! You can only upload ${freeLimit} images on the free plan. Please purchase Listing Credits to add more. Price: NGN ${price}`);
          e.target.value = ""; 
          return;
      }

      processFile(e.target.files[0], viewType);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.store_id) {
        alert("Please select a store for this item.");
        return;
    }

    setSaving(true);
    try {
        const payload = {
            ...formData,
            price: parseFloat(formData.price),
            discount_percentage: parseFloat(formData.discount_percentage) || 0, // <--- SEND DISCOUNT
            stock_quantity: parseInt(formData.stock_quantity),
            weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        };
        if (isCustomCategory) payload.category_id = null;
        else payload.custom_category = null;
        if (isCustomBrand) payload.brand_id = null;
        else payload.custom_brand = null;

        if (editingPart) {
            await partsApi.updateMyPart(editingPart.id, payload);
        } else {
            await partsApi.createMyPart(payload);
        }
        fetchInventory();
        handleCloseModal();
    } catch (err) {
        alert(`Failed to save part: ${err.message || err.data?.message}`);
    } finally {
        setSaving(false);
    }
  };

  const handleDelete = async (partId) => {
    if (window.confirm("Delete this part?")) {
        try {
            await partsApi.deleteMyPart(partId);
            setParts(prev => prev.filter(p => p.id !== partId));
        } catch (err) {
            alert("Failed to delete part.");
        }
    }
  };

  const handleOpenBoost = (part) => {
      setBoostTarget(part);
      setIsBoostModalOpen(true);
  };

  // Helper to calculate final price for display in modal
  const calculateFinalPrice = () => {
      const price = parseFloat(formData.price) || 0;
      const discount = parseFloat(formData.discount_percentage) || 0;
      if (discount <= 0) return price;
      return price - (price * (discount / 100));
  };

  if (loading) return <div className="text-center py-10">Loading inventory...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FaBoxOpen className="text-primary" /> My Shop Inventory
        </h2>
        <div className="flex gap-2">
            <button 
              onClick={() => handleOpenModal()}
              className="bg-primary hover:bg-primary-dark text-white text-sm font-bold py-2 px-4 rounded flex items-center gap-2 transition duration-300"
            >
              <FaPlus /> Add Part
            </button>
        </div>
      </div>

      {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

      {!Array.isArray(parts) || parts.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">Your shop is empty.</p>
          <button onClick={() => handleOpenModal()} className="text-primary hover:underline">Add your first item</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {parts.map((part) => {
            if (!part) return null;
            const isPromoted = part.is_promoted && new Date(part.promoted_until) > new Date();

            return (
                <div key={part.id || Math.random()} className={`bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow flex gap-4 ${isPromoted ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'}`}>
                <div className="w-24 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative">
                    <img 
                        src={part.itemurl || (part.images && part.images.front_jpg) || 'https://via.placeholder.com/150'} 
                        alt={part.itemname || 'Part'} 
                        className="w-full h-full object-cover" 
                    />
                    {part.is_oem && (
                        <span className="absolute top-0 left-0 bg-blue-600 text-white text-[8px] px-1 py-0.5 font-bold">OEM</span>
                    )}
                    {isPromoted && (
                        <span className="absolute bottom-0 left-0 right-0 bg-red-600 text-white text-[8px] px-1 py-0.5 font-bold text-center">BOOSTED</span>
                    )}
                </div>
                <div className="flex-grow">
                    <h3 className="font-bold text-gray-800 line-clamp-1">{part.itemname || 'Unnamed Part'}</h3>
                    <p className="text-xs text-gray-500 mb-1">{part.make} {part.model} | {part.condition}</p>
                    <div className="flex justify-between items-center mt-2">
                        <span className="font-bold text-primary">{helpers.formatCurrency(part.price, 'NGN')}</span>
                        <span className={`text-xs px-2 py-1 rounded ${part.stock_quantity > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            Stock: {part.stock_quantity}
                        </span>
                    </div>
                    {part.discount_percentage > 0 && (
                        <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded font-bold mt-1 inline-block">-{part.discount_percentage}% OFF</span>
                    )}
                    <div className="flex gap-2 mt-3">
                        {/* BOOST BUTTON */}
                        <button 
                            onClick={() => handleOpenBoost(part)}
                            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 flex items-center gap-1 font-bold"
                        >
                            <FaRocket /> Boost
                        </button>

                        <button onClick={() => handleOpenModal(part)} className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 flex items-center gap-1"><FaEdit /> Edit</button>
                        <button onClick={() => handleDelete(part.id)} className="text-xs bg-red-50 text-red-600 px-3 py-1 rounded hover:bg-red-100 flex items-center gap-1"><FaTrash /> Delete</button>
                    </div>
                </div>
                </div>
            );
          })}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b">
                    <h3 className="text-xl font-bold">{editingPart ? 'Edit Item' : 'Add New Item'}</h3>
                    <button onClick={handleCloseModal}><FaTimes /></button>
                </div>
                
                <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
                    
                    <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-4">
                        <label className="block text-sm font-bold text-blue-800 mb-1 flex items-center gap-2">
                            <FaStore /> Select Store Location *
                        </label>
                        {myStores.length > 0 ? (
                            <select 
                                name="store_id" 
                                value={formData.store_id} 
                                onChange={handleInputChange} 
                                required
                                className="p-2 border rounded w-full bg-white focus:ring-2 focus:ring-blue-500"
                            >
                                {myStores.map(store => (
                                    <option key={store.id} value={store.id}>
                                        {store.store_name} ({store.lga_name})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <p className="text-red-500 text-sm">You have no active stores. Please register a store first.</p>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Item Name *</label>
                            <input name="itemname" value={formData.itemname} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Part Number (Optional)</label>
                            <input name="part_number" value={formData.part_number} onChange={handleInputChange} placeholder="e.g. 04465-0K090" className="p-2 border rounded w-full" />
                        </div>
                    </div>

                    {/* --- PRICING & DISCOUNT SECTION --- */}
                    <div className="bg-blue-50 p-4 rounded border border-blue-100">
                        <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2"><FaTags /> Pricing & Discounts</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Original Price (NGN) *</label>
                                <input type="number" name="price" value={formData.price} onChange={handleInputChange} required className="w-full p-2 border rounded" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Discount (%)</label>
                                <input 
                                    type="number" 
                                    name="discount_percentage" 
                                    value={formData.discount_percentage} 
                                    onChange={handleInputChange} 
                                    min="0" 
                                    max="100"
                                    className="w-full p-2 border rounded" 
                                    placeholder="0"
                                />
                            </div>
                            <div className="bg-white p-2 rounded border border-blue-200 text-right">
                                <span className="block text-xs text-gray-500 uppercase font-bold">Final Price</span>
                                <span className="text-lg font-bold text-green-600 flex items-center justify-end gap-2">
                                    <FaCalculator className="text-gray-400 text-xs" />
                                    {helpers.formatCurrency(calculateFinalPrice(), 'NGN')}
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* ---------------------------------- */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Stock Quantity *</label>
                            <input type="number" name="stock_quantity" value={formData.stock_quantity} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1 flex items-center gap-1"><FaWeightHanging /> Weight (kg)</label>
                            <input type="number" step="0.1" name="weight_kg" value={formData.weight_kg} onChange={handleInputChange} placeholder="e.g. 0.5" className="p-2 border rounded w-full" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Category</label>
                            <select 
                                name="category_id" 
                                value={isCustomCategory ? 'other' : formData.category_id} 
                                onChange={handleInputChange} 
                                className="p-2 border rounded w-full bg-white"
                            >
                                {Object.keys(CATEGORY_GROUPS).map(group => (
                                    <optgroup key={group} label={group}>
                                        {CATEGORY_GROUPS[group].map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.label}</option>
                                        ))}
                                    </optgroup>
                                ))}
                                <option value="other" className="font-bold text-blue-600">Others (Specify)</option>
                            </select>
                            {isCustomCategory && (
                                <input 
                                    type="text" name="custom_category" 
                                    value={formData.custom_category} onChange={handleInputChange}
                                    placeholder="Enter custom category"
                                    className="mt-2 p-2 border border-blue-300 rounded w-full focus:ring-2 focus:ring-blue-500"
                                />
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Brand</label>
                            <select 
                                name="brand_id" 
                                value={isCustomBrand ? 'other' : formData.brand_id} 
                                onChange={handleInputChange} 
                                className="p-2 border rounded w-full bg-white"
                            >
                                {PART_BRANDS.map(b => (
                                    <option key={b.id} value={b.id}>{b.label}</option>
                                ))}
                                <option value="other" className="font-bold text-blue-600">Others (Specify)</option>
                            </select>
                            {isCustomBrand && (
                                <input 
                                    type="text" name="custom_brand" 
                                    value={formData.custom_brand} onChange={handleInputChange}
                                    placeholder="Enter custom brand"
                                    className="mt-2 p-2 border border-blue-300 rounded w-full focus:ring-2 focus:ring-blue-500"
                                />
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-600 mb-1">Condition</label>
                            <select name="condition" value={formData.condition} onChange={handleInputChange} className="p-2 border rounded w-full bg-white">
                                <option>New (In Box)</option>
                                <option>New (Open Box)</option>
                                <option>Remanufactured</option>
                                <option>Used (Tested)</option>
                                <option>Used (Untested)</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded border border-blue-100">
                        <div>
                            <label className="block text-xs font-bold text-blue-800 mb-1">Pay on Delivery</label>
                            <select 
                                name="pay_on_delivery_status" 
                                value={formData.pay_on_delivery_status} 
                                onChange={handleInputChange} 
                                className="p-2 border rounded w-full bg-white"
                            >
                                <option value="Available">Available</option>
                                <option value="Not Available">Not Available</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-blue-800 mb-1">Refund Policy</label>
                            <select 
                                name="refundable_status" 
                                value={formData.refundable_status} 
                                onChange={handleInputChange} 
                                className="p-2 border rounded w-full bg-white"
                            >
                                <option value="Refundable">Refundable</option>
                                <option value="Non-Refundable">Non-Refundable</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 bg-gray-50 p-3 rounded border border-gray-200">
                        <input 
                            type="checkbox" 
                            name="is_oem" 
                            checked={formData.is_oem} 
                            onChange={handleInputChange} 
                            className="h-5 w-5 text-primary rounded" 
                        />
                        <div>
                            <span className="text-sm font-bold text-gray-800">Genuine OEM Part?</span>
                            <p className="text-xs text-gray-600">Check this if the part is an original manufacturer part (e.g. Toyota Box), not aftermarket.</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1">Description</label>
                        <textarea 
                            name="description" 
                            value={formData.description} 
                            onChange={handleInputChange} 
                            className="p-2 border rounded w-full h-24"
                            placeholder="Describe the part details, compatibility notes, etc..."
                        ></textarea>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2">Images (Primary is required)</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {IMAGE_SLOTS.map(slot => (
                                <div key={slot.key} className="relative border-2 border-dashed rounded h-32 flex flex-col items-center justify-center text-center p-1 hover:bg-gray-50 transition-colors group">
                                    
                                    {uploadingSlot === slot.key ? (
                                        <div className="flex flex-col items-center text-primary">
                                            <FaSpinner className="animate-spin text-xl mb-1" />
                                            <span className="text-[10px]">Uploading...</span>
                                        </div>
                                    ) : formData.images[slot.key] ? (
                                        <>
                                            <img src={formData.images[slot.key]} alt={slot.label} className="w-full h-full object-cover rounded" />
                                            <button 
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, images: { ...prev.images, [slot.key]: null } }))}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <FaTrash size={10} />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-center gap-2 w-full px-2">
                                            
                                            {/* --- CONDITIONAL CAMERA BUTTON --- */}
                                            {premiumConfig?.camera_feature_enabled && (
                                                <button 
                                                    type="button"
                                                    onClick={() => handleCameraClick(slot.key)}
                                                    className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-md transition-transform hover:scale-110"
                                                    title="Snap & Save"
                                                >
                                                    <FaCamera />
                                                </button>
                                            )}
                                            
                                            {premiumConfig?.camera_feature_enabled && <span className="text-xs text-gray-400">- OR -</span>}
                                            
                                            <label className="cursor-pointer text-gray-500 hover:text-primary flex flex-col items-center">
                                                <FaFolderOpen className="text-xl" />
                                                <span className="text-[10px] mt-1">Browse</span>
                                                <input 
                                                    type="file" 
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => handleFileInput(e, slot.key)}
                                                />
                                            </label>
                                        </div>
                                    )}
                                    <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[9px] py-1 px-1 truncate">
                                        {slot.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-600">Cancel</button>
                        <button type="submit" disabled={saving || uploading} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center gap-2">
                            {saving ? 'Saving...' : <><FaSave /> Save Item</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      <CameraModal 
          isOpen={isCameraOpen} 
          onClose={() => setIsCameraOpen(false)} 
          onCapture={handleCameraCapture}
          viewType={activeViewType || 'image'}
      />

      {/* BOOST MODAL */}
      {isBoostModalOpen && boostTarget && (
          <BoostModal 
              isOpen={isBoostModalOpen}
              onClose={() => setIsBoostModalOpen(false)}
              itemId={boostTarget.id}
              itemType="part"
              itemName={boostTarget.itemname}
          />
      )}
    </div>
  );
}

export default MyStoreInventory;
